// Single row in the members list. NEVER renders any §15.3 sensitive field
// beyond what the API already chose to expose (display name, @username, last
// active relative time). The Member.telegramId is masked via formatHandle's
// fallback when there's no public @handle — see the lib/format comment.

import type { MemberListItem } from "@community-analytics/shared";

import { cn } from "@shared/lib";

import { formatHandle, formatLastActive } from "../lib/format";

import { StageBadge } from "./StageBadge";

export interface MemberRowProps {
  member: MemberListItem;
  /** Externalized stage label (i18n-ready). Caller picks the dictionary. */
  stageLabel: string;
  /** Optional override for the lurker label ("Never active" by default). */
  lurkerLabel?: string;
  /** Fallback display name when Telegram returned null (e.g. kicked). */
  unknownNameLabel: string;
  className?: string;
}

export function MemberRow({
  member,
  stageLabel,
  lurkerLabel,
  unknownNameLabel,
  className,
}: MemberRowProps) {
  const displayName =
    member.displayName && member.displayName.length > 0
      ? member.displayName
      : unknownNameLabel;
  const handle = formatHandle(member.username, member.telegramId);
  const lastActive = formatLastActive(
    member.lastActiveAt,
    lurkerLabel === undefined ? {} : { lurkerLabel },
  );
  return (
    <li
      className={cn(
        "flex items-center justify-between gap-3 rounded-lg border bg-background px-4 py-3",
        className,
      )}
      data-member-id={member.id}
    >
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">{displayName}</span>
        <span className="truncate text-xs text-muted-foreground">
          {handle}
        </span>
      </div>
      <div className="flex flex-none flex-col items-end gap-1">
        <StageBadge stage={member.lifecycleStage} label={stageLabel} />
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {lastActive}
        </span>
      </div>
    </li>
  );
}
